import { useCallback, useEffect, useState } from 'react';
import { getRefundPolicy } from '../api/pharmacyDispense';

/**
 * Facility refund policy (`cash_refund_prohibited`), read from the EMR gateway.
 *
 * This only decides which controls the screen shows. The gateway and the
 * dispensing service enforce the rule on their own, so a stale or failed read
 * here can never let a cash refund through -- it can only show the wrong button.
 * Failing open (flag = false) therefore keeps today's behaviour for facilities
 * that never enabled the flag, and the server rejects any forbidden settle.
 */
const CACHE_TTL_MS = 60 * 1000;

let cached = null; // { value: boolean, at: number }
let inflight = null;

function fetchPolicy() {
  if (inflight) return inflight;
  inflight = getRefundPolicy()
    .then((data) => {
      const value = Boolean(data?.cash_refund_prohibited);
      cached = { value, at: Date.now() };
      return value;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export default function usePharmacyRefundPolicy() {
  const fresh = cached && Date.now() - cached.at < CACHE_TTL_MS;
  const [cashRefundProhibited, setCashRefundProhibited] = useState(fresh ? cached.value : false);
  const [loading, setLoading] = useState(!fresh);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCashRefundProhibited(await fetchPolicy());
    } catch (err) {
      setError(err?.message || 'Could not load the refund policy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) return;
    reload();
  }, [reload]);

  return { cashRefundProhibited, loading, error, reload };
}
